import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import type { ReportStatus } from "@/src/pages/Reports/reports.type";

/**
 * Borne de sécurité de la file.
 *
 * Le serveur plafonne à 200 et ne pagine pas. Sans limite explicite on
 * dépendrait de son défaut (50), qui peut changer ; avec celle-ci, le nombre de
 * cartes rendues est connu.
 *
 * ⚠️ **`total` ne s'y compare plus.** Depuis que le serveur en a fait un vrai
 * `count(*)`, il compte les signalements **au statut demandé** sans se soucier
 * de la limite : `total > reports.length` signifie donc que la file déborde de
 * cette page — c'est là qu'il faudra paginer, et le compteur des bascules le
 * dira avant nous (cf. `useReportCounts`).
 */
export const REPORTS_LIMIT = 200;

/**
 * La file de modération, filtrée par statut.
 *
 * Modèle de hook de query (cf. `useWhoami`) : on expose une API lisible, pas
 * l'objet React Query brut.
 *
 * ⚠️ Le serveur trie du **plus ancien au plus récent** — l'inverse de
 * l'habitude — parce que la directive App Store 1.2 impose de traiter un
 * contenu signalé sous 24 h. On ne retrie pas côté client : le premier de la
 * liste est celui qui presse.
 */
export function useReports(status: ReportStatus) {
  const $api = useApiClient();
  const t = useTranslations("reports");

  const { data, error, isError, isLoading, refetch } = $api.useQuery(
    "get",
    "/api/v1/moderation/reports",
    { params: { query: { status, limit: REPORTS_LIMIT } } },
  );

  useQueryErrorToast(isError, t("load-error"), error);

  return {
    reports: data?.data?.reports ?? [],
    total: data?.data?.total ?? 0,
    isLoading,
    isError,
    refetch,
  };
}
